// Pure helpers for data files in the editor: which extensions count as
// data, which DuckDB reader reads each one, and which data paths a
// program names in quoted string literals. No imports, so the node test
// harness can sandbox this module.

export type FileKind = 'code' | 'data';

/// Extensions PROC IMPORT lowers to a DuckDB reader (see readerForDbms
/// in src/sql_build.zig). Lower-case, without the dot.
export const DATA_EXTENSIONS: readonly string[] = [
  'csv', 'tsv', 'json', 'parquet', 'sas7bdat', 'xpt', 'sav', 'zsav', 'por', 'dta',
];

/// Lower-case extension of the last path segment, '' when there is none.
/// A leading dot (`.env`) is not an extension.
export function extensionOf(path: string): string {
  const base = path.slice(path.lastIndexOf('/') + 1);
  const dot = base.lastIndexOf('.');
  if (dot <= 0) return '';
  return base.slice(dot + 1).toLowerCase();
}

/// `code` for .sas, `data` for the PROC IMPORT set, null for anything else.
export function classifyPath(path: string): FileKind | null {
  const ext = extensionOf(path);
  if (ext === 'sas') return 'code';
  if (DATA_EXTENSIONS.includes(ext)) return 'data';
  return null;
}

function sqlString(text: string): string {
  return `'${text.replace(/'/g, "''")}'`;
}

/// DuckDB relation expression that reads a data file registered under
/// `path`. Mirrors readerForDbms in src/sql_build.zig: csv, tsv, json,
/// and parquet are DuckDB built-ins; the stat formats go through
/// stats_duck's read_stat, which detects the format from the extension.
export function readerFor(path: string): string {
  const p = sqlString(path);
  switch (extensionOf(path)) {
    case 'csv':
      return `read_csv_auto(${p})`;
    case 'tsv':
      return `read_csv(${p}, delim='\\t', header=true)`;
    case 'json':
      return `read_json_auto(${p})`;
    case 'parquet':
      return `read_parquet(${p})`;
    default:
      return `read_stat(${p})`;
  }
}

/// Double-quote an identifier, doubling embedded quotes.
export function quoteIdent(name: string): string {
  return `"${name.replace(/"/g, '""')}"`;
}

/// Fully quoted WORK relation for the dataset viewer.
export function workRelation(table: string): string {
  return `"work".${quoteIdent(table)}`;
}

/// Normalise a quoted literal to the tree's path shape for matching:
/// forward slashes, no leading `./`, trimmed, lower-case.
export function normalizeLiteralPath(literal: string): string {
  let s = literal.replace(/\\/g, '/').trim();
  while (s.startsWith('./')) s = s.slice(2);
  return s.toLowerCase();
}

const QUOTED_RE = /'([^']*)'|"([^"]*)"/g;

/// Every candidate path that appears in a quoted string literal of
/// `program`, matched case-insensitively after normalisation. The
/// result keeps the order of `candidates` and has no duplicates.
/// Paths built by macro code are not found; the user loads those from
/// the tree instead.
export function scanQuotedPaths(program: string, candidates: readonly string[]): string[] {
  const byNorm = new Map<string, string[]>();
  for (const c of candidates) {
    const key = normalizeLiteralPath(c);
    const list = byNorm.get(key);
    if (list) list.push(c);
    else byNorm.set(key, [c]);
  }
  const hits = new Set<string>();
  for (const m of program.matchAll(QUOTED_RE)) {
    const literal = m[1] ?? m[2] ?? '';
    if (literal.length === 0) continue;
    const matched = byNorm.get(normalizeLiteralPath(literal));
    if (matched) for (const c of matched) hits.add(c);
  }
  return candidates.filter((c) => hits.has(c));
}

/// Human-readable size for the tree: `512 B`, `1.5 KB`, `20 KB`,
/// `1.2 MB`, `3.0 GB`. Empty string for a bad input.
export function formatSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '';
  if (bytes < 1024) return `${Math.round(bytes)} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb < 10 ? kb.toFixed(1) : Math.round(kb)} KB`;
  const mb = kb / 1024;
  if (mb < 1024) return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`;
  return `${(mb / 1024).toFixed(1)} GB`;
}
